import { msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import type { BackendTypes } from '@tf/api';
import type { FormControl } from '@tf/form-builder';
import { getSegmentsForReview, selectForm } from '@tf/shared';
import { Button, Icon, TFNotifier } from '@tf/ui';
import { type SubmitHandler, T } from '@tf/utils';
import type React from 'react';
import { useEffect, useRef } from 'react';
import { Form, useApplication, useApplicationMainReview } from '@/components/application';
import { ErrorCard, FormLoadingCard, useMenu } from '@/components/shared';
import { useSegmentsMutation, useSegmentsQuery } from '@/core/api';
import { useDefinitions, useFormControlRefs } from '@/core/providers';

import { usePageTitle } from '../../usePageTitle';

import { useApplicationFormParams } from './params';

export const ApplicationFormRoute: React.FC = () => {
	const { _ } = useLingui();
	const { formName, formHash } = useApplicationFormParams();

	const definitions = useDefinitions();

	const application = useApplication();
	const mainReview = useApplicationMainReview();

	const formDef = selectForm({
		formDefs: definitions.defs.forms,
		segmentIdentities: getSegmentsForReview(application.account, mainReview),
		formName,
		hash: formHash,
	});

	const formControlRef = useRef<FormControl>(null);
	const submitTypeRef = useRef<'save' | 'continue' | 'quietSave'>('save');
	const { menuItems } = useMenu();
	const formIndex = menuItems.findIndex((item) => item.isSelected);

	usePageTitle(formDef.label);

	const { setFormControlRef, setSubmitTypeRef } = useFormControlRefs();
	useEffect(() => {
		setFormControlRef(formControlRef);
		setSubmitTypeRef(submitTypeRef);

		return () => {
			setFormControlRef(null);
			setSubmitTypeRef(null);
		};
	}, [setSubmitTypeRef, setFormControlRef]);

	const segmentsQuery = useSegmentsQuery(formDef.segmentIdentities);

	const segmentsMutation = useSegmentsMutation();

	const onSubmit: SubmitHandler = async ({ values, segmentValidationStatuses }) => {
		try {
			T.assertNotNullish(segmentsQuery.data, 'no segment containers loaded');

			const data: BackendTypes.SegmentsWritePayload = segmentsQuery.data.map((sc) => {
				const segmentKind = sc.container.info.SegmentIdentity.segmentKind;

				return {
					segment: values[segmentKind],
					validationStatus: segmentValidationStatuses[segmentKind],
					segmentIdentity: sc.container.info.SegmentIdentity,
					revision: sc.container.info.Revision,
				};
			});

			await segmentsMutation.mutateAsync({ data });
			if (submitTypeRef.current === 'quietSave') return;

			const hasErrors = Object.values(segmentValidationStatuses).some((s) => s === 'VALIDATION_ERRORS');

			if (submitTypeRef.current === 'continue' && !hasErrors) {
				const nextForm = menuItems[formIndex + 1];
				nextForm.onClick();
				window.scrollTo(0, 0);
			}

			TFNotifier.success(
				_(
					msg({
						id: 'applicationFormPage.notification.saveSucceeded',
						message: 'Changes saved',
					}),
				),
			);
		} catch (_e) {
			if (submitTypeRef.current === 'quietSave') return;
			TFNotifier.error(
				_(
					msg({
						id: 'applicationFormPage.notification.saveFailed',
						message: 'Failed to save changes',
					}),
				),
			);
		}
	};

	if (segmentsQuery.isPending) {
		return <FormLoadingCard />;
	}

	if (segmentsQuery.error) {
		return (
			<ErrorCard
				icon={<Icon.IconExclamationCircle />}
				title={_(msg({ id: 'applicationFormPage.loadingError.title', message: 'Failed to load form' }))}
				description={_(msg({ id: 'applicationFormPage.loadingError.message', message: 'Please try again' }))}
				primaryAction={
					<Button mt="1rem" type="button" onClick={() => segmentsQuery.refetch()}>
						{_(
							msg({
								id: 'applicationFormPage.loadingError.action',
								message: 'Reload form',
							}),
						)}
					</Button>
				}
			/>
		);
	}

	return (
		<Form
			key={`${formName}.${formHash}`}
			label={formDef.label}
			description={formDef.description}
			segmentContainers={segmentsQuery.data?.map((d) => d.container)}
			onSubmit={onSubmit}
			submitTypeRef={submitTypeRef}
			formControlRef={formControlRef}
		/>
	);
};
